import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAdminToken, getAdminToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Lock } from "lucide-react";
import { BrandLockup } from "@/components/BrandLockup";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getAdminToken()) navigate("/admin");
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { email, password });
      setAdminToken(data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-foreground px-4">
      <div className="w-full max-w-sm bg-card rounded-3xl p-8 shadow-2xl">
        <div className="flex flex-col items-start gap-1 mb-6">
          <BrandLockup />
          <span className="text-[10px] font-bold tracking-[0.28em] uppercase text-muted-foreground ml-1">
            Tap. Book. Celebrate.
          </span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <Lock className="h-4 w-4 text-primary" />
          <h1 className="font-display font-bold text-2xl">Admin login</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">Manage vendors, services & bookings.</p>
        <form onSubmit={submit} className="grid gap-3">
          <Input data-testid="admin-email" placeholder="Email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} />
          <Input data-testid="admin-password" placeholder="Password" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} />
          {error && <p data-testid="admin-login-error" className="text-sm text-destructive">{error}</p>}
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="h-11 bg-primary text-white rounded-full font-semibold disabled:opacity-60"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
